// Shared HTTP client for v2.
//
// Production-grade behavior:
//   * always sends credentials (cookies) — backend is on same origin in prod
//     and CORS-enabled with credentials in dev.
//   * on 401, transparently calls POST /oauth/refresh once for idempotent
//     methods, then retries. If refresh fails, redirects to /?error=
//     session_expired so the Landing page can show a re-connect CTA.
//   * surfaces structured errors (status + detail) for callers that want
//     to render rate-limit / concurrency-cap messages.

const IDEMPOTENT = new Set(["GET", "HEAD", "OPTIONS"]);

export interface ApiError extends Error {
  status: number;
  detail?: string;
  payload?: unknown;
}

export interface FetchOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  // When true, do NOT redirect on 401 — return null instead. Useful for
  // /oauth/me where an unauthenticated user is the normal case.
  silentAuth?: boolean;
  // When set, override the default JSON Content-Type.
  rawBody?: BodyInit;
  signal?: AbortSignal;
}

async function rawFetch(path: string, opts: FetchOpts, retried = false): Promise<Response> {
  const init: RequestInit = {
    method: opts.method ?? "GET",
    credentials: "include",
    signal: opts.signal,
  };
  if (opts.rawBody !== undefined) {
    init.body = opts.rawBody;
  } else if (opts.body !== undefined) {
    init.headers = { "Content-Type": "application/json" };
    init.body = JSON.stringify(opts.body);
  }
  const res = await fetch(path, init);
  if (
    res.status === 401 &&
    !retried &&
    !opts.silentAuth &&
    IDEMPOTENT.has(init.method ?? "GET")
  ) {
    const refresh = await fetch("/oauth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (refresh.ok) {
      return rawFetch(path, opts, true);
    }
    // Don't navigate the page out from underneath an in-progress AI
    // job, sheet, or form. Surface session-expired as an event the
    // shell can catch and render as a banner; consumers that want the
    // hard redirect can opt in by listening to it.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("qb:session-expired"));
    }
  }
  return res;
}

export async function apiCall<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  const res = await rawFetch(path, opts);
  if (!res.ok) {
    let detail: string | undefined;
    let payload: unknown;
    try {
      payload = await res.json();
      const p = payload as { detail?: string; message?: string };
      detail = p.detail || p.message;
    } catch {
      try {
        detail = await res.text();
      } catch {
        // ignore
      }
    }
    const err = new Error(detail || `HTTP ${res.status}`) as ApiError;
    err.status = res.status;
    err.detail = detail;
    err.payload = payload;
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const ctype = res.headers.get("content-type") || "";
  if (ctype.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

// Soft variant: returns null on 4xx/5xx instead of throwing. Useful for
// optional surfaces (e.g. notifications, favorites) where we don't want
// a single failed call to crash a panel.
export async function apiSoft<T>(path: string, opts: FetchOpts = {}): Promise<T | null> {
  try {
    return await apiCall<T>(path, opts);
  } catch (err) {
    const status = (err as ApiError).status;
    if (status === 401 || status === 403) return null;
    console.warn(`[v2 api] ${path} -> ${status ?? "?"}`);
    return null;
  }
}
